import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BookOpen, Lightbulb, MessageSquare, Sparkles, ArrowRight, RefreshCw, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import PageSubHeader from '@/components/PageSubHeader';
import CustomPlanWizardModal from '@/components/CustomPlanWizardModal';
import SharePlanRecapModal from '@/components/SharePlanRecapModal';
import { useSafeBack } from '@/utils/useSafeBack';
import { API_URL as API } from '@/utils/apiConfig';

const POLL_MS = 2500;

// Dark plan covers vanish on dark themes; fall back to the theme accent.
const pickAccent = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 'var(--theme-accent)';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 0.35 ? 'var(--theme-accent)' : hex;
};
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

export default function PlanRecapPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeBack(`/study-plans/${planId}`);
  const [plan, setPlan] = useState(null);
  const [state, setState] = useState({ status: 'loading', recap: null });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const kickOff = useCallback(async () => {
    setState({ status: 'generating', recap: null });
    try {
      const res = await axios.post(`${API}/reading-plans/${planId}/recap`, {}, { withCredentials: true });
      setState(res.data);
    } catch (err) {
      setState({ status: 'error', recap: null, detail: err.response?.data?.detail });
    }
  }, [planId]);

  useEffect(() => {
    axios.get(`${API}/reading-plans/${planId}`, { withCredentials: true })
      .then((res) => setPlan(res.data?.plan || null)).catch(() => {});
    axios.get(`${API}/reading-plans/${planId}/recap`, { withCredentials: true })
      .then((res) => {
        if (res.data.status === 'none' && res.data.finished) kickOff();
        else if (!res.data.finished && res.data.status !== 'ready') setState({ status: 'locked', recap: null });
        else setState(res.data);
      })
      .catch((err) => setState({ status: 'error', recap: null, detail: err.response?.data?.detail }));
  }, [planId, kickOff]);

  useEffect(() => {
    if (state.status !== 'generating') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/reading-plans/${planId}/recap`, { withCredentials: true });
        if (res.data.status !== 'generating') setState(res.data);
      } catch { /* keep polling */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [state.status, planId]);

  const cover = pickAccent(plan?.cover_color);
  const recap = state.recap;

  const discuss = () => {
    const takeaways = (recap?.takeaways || []).map((t, i) => `${i + 1}. ${t}`).join('\n');
    navigate('/chat', {
      state: {
        seedMessage: `I just finished my study plan "${plan?.title}". My key takeaways were:\n${takeaways}\n\nHelp me keep living these out this week.`,
        autoSend: true,
        source: 'plan_recap',
      },
    });
  };

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }} data-testid="plan-recap-page">
      <Header />
      <PageSubHeader title="Plan Recap" onBack={goBack} />
      <main className="page-content max-w-2xl mx-auto w-full px-5 py-6 pb-24">
        {state.status === 'loading' || state.status === 'generating' ? (
          <RecapPending cover={cover} generating={state.status === 'generating'} />
        ) : state.status === 'ready' && recap ? (
          <RecapBody recap={recap} plan={plan} cover={cover} onDiscuss={discuss} onRelated={() => setWizardOpen(true)} onShare={() => setShareOpen(true)} />
        ) : (
          <RecapProblem state={state} cover={cover} onRetry={kickOff} onBack={() => navigate(`/study-plans/${planId}`)} />
        )}
      </main>
      {shareOpen && recap && (
        <SharePlanRecapModal planId={planId} plan={plan} recap={recap} onClose={() => setShareOpen(false)} />
      )}
      <CustomPlanWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => { setWizardOpen(false); navigate('/study-plans'); }}
        initialTopic={recap?.related_plan?.topic || ''}
        initialObjective={recap?.related_plan?.objective || ''}
      />
    </div>
  );
}

function RecapPending({ cover, generating }) {
  return (
    <div className="flex flex-col items-center text-center py-20" data-testid="plan-recap-pending">
      <Loader2 className="h-7 w-7 animate-spin mb-4" style={{ color: cover }} />
      <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
        {generating ? 'Eili is gathering the highlights of your journey…' : 'Loading your recap…'}
      </p>
    </div>
  );
}

function RecapProblem({ state, cover, onRetry, onBack }) {
  const locked = state.status === 'locked';
  return (
    <div className="text-center py-16" data-testid="plan-recap-problem">
      <p className="text-sm mb-5" style={{ color: 'var(--theme-textSecondary)' }}>
        {locked
          ? 'Finish every day of this plan to unlock your recap.'
          : (typeof state.detail === 'string' && state.detail) || "Eili couldn't put your recap together just now."}
      </p>
      {locked ? (
        <Button onClick={onBack} variant="outline" className="rounded-full" data-testid="plan-recap-back-btn">Back to plan</Button>
      ) : (
        <Button onClick={onRetry} className="rounded-full gap-2" style={{ background: cover, color: '#fff' }} data-testid="plan-recap-retry-btn">
          <RefreshCw className="h-4 w-4" /> Try again
        </Button>
      )}
    </div>
  );
}

function Section({ label, cover, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: cover }}>{label}</p>
      {children}
    </section>
  );
}

function RecapBody({ recap, plan, cover, onDiscuss, onRelated, onShare }) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="plan-recap-content">
      <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: cover }}>
        {plan?.title} · {plan?.length_days}-day recap
      </p>
      <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4" style={{ color: 'var(--theme-text)' }} data-testid="plan-recap-headline">
        {recap.headline}
      </h1>
      <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'var(--theme-textSecondary)' }} data-testid="plan-recap-summary">
        {recap.summary}
      </p>

      {recap.themes?.length > 0 && (
        <Section label="Themes you explored" cover={cover} testId="plan-recap-themes">
          <div className="flex flex-wrap gap-2">
            {recap.themes.map((th, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-full" style={{ background: tint(cover, 12), color: cover, border: `1px solid ${tint(cover, 35)}` }}>{th}</span>
            ))}
          </div>
        </Section>
      )}

      <Section label="Key takeaways" cover={cover} testId="plan-recap-takeaways">
        <ol className="space-y-3">
          {recap.takeaways.map((tk, i) => (
            <li key={i} className="flex gap-3 rounded-2xl p-4" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }} data-testid={`plan-recap-takeaway-${i}`}>
              <span className="h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: cover, color: '#fff' }}>{i + 1}</span>
              <p className="text-[14px] leading-relaxed" style={{ color: 'var(--theme-text)' }}>{tk}</p>
            </li>
          ))}
        </ol>
      </Section>

      {recap.key_passages?.length > 0 && (
        <Section label="Passages to return to" cover={cover} testId="plan-recap-passages">
          <div className="space-y-2">
            {recap.key_passages.map((kp, i) => (
              <div key={i} className="flex gap-3 items-start">
                <BookOpen className="h-4 w-4 mt-0.5 shrink-0" style={{ color: cover }} />
                <p className="text-[14px] leading-relaxed" style={{ color: 'var(--theme-text)' }}>
                  <span className="font-semibold">{kp.label}</span>
                  <span style={{ color: 'var(--theme-textSecondary)' }}> — {kp.why}</span>
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {recap.carry_forward && (
        <div className="rounded-2xl p-5 mb-8 flex gap-3" style={{ background: tint(cover, 8), border: `1px solid ${tint(cover, 25)}` }} data-testid="plan-recap-carry-forward">
          <Lightbulb className="h-4 w-4 mt-0.5 shrink-0" style={{ color: cover }} />
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--theme-text)' }}>{recap.carry_forward}</p>
        </div>
      )}

      <Button onClick={onDiscuss} className="w-full rounded-2xl py-5 mb-3 gap-2 font-semibold" style={{ background: cover, color: '#fff' }} data-testid="plan-recap-discuss-btn">
        <MessageSquare className="h-4 w-4" /> Discuss my takeaways with Eili
      </Button>
      <Button onClick={onShare} variant="outline" className="w-full rounded-2xl py-5 mb-3 gap-2 font-semibold" style={{ borderColor: tint(cover, 45), color: 'var(--theme-text)' }} data-testid="plan-recap-share-btn">
        <Share2 className="h-4 w-4" style={{ color: cover }} /> Share what I learned
      </Button>

      {recap.related_plan?.topic && (
        <button
          type="button"
          onClick={onRelated}
          className="group w-full text-left rounded-2xl p-4 flex gap-3 items-center transition-[transform,box-shadow] hover:-translate-y-[1px] hover:shadow-lg"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          data-testid="plan-recap-related-btn"
        >
          <Sparkles className="h-5 w-5 shrink-0" style={{ color: cover }} />
          <div className="flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: cover }}>Start a related plan</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{recap.related_plan.topic}</p>
            {recap.related_plan.reason && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{recap.related_plan.reason}</p>
            )}
          </div>
          <ArrowRight className="h-4 w-4 shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" style={{ color: cover }} />
        </button>
      )}
    </div>
  );
}
